// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { showError, showSuccess } from '@calendly/alert';
import { Form, RadioGroupField, SubmitFormButton, useFormContext } from '@calendly/form';
import { usePageTitle } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { PageHeader } from '@calendly/ui/components/page-header';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { DirtyFormTracking } from 'components/core/form';
import LoadingState from 'components/core/loading_state';
import { TabContainer } from 'components/page_elements';
import { useGetMeetingIntelligenceUserSettingsQuery, useUpdateOrganizationMeetingIntelligenceDataMutation } from 'features/meeting_intelligence';
import { SettingsModalFooterActions } from 'features/settings_modal/footer_actions';
import { useIsSettingsModalLayout, useSettingsModalLayoutOptional } from 'features/settings_modal/layout_context';
const formCss = "f1595zjf";
const optionDescriptionCss = "oz11jn";
const NotetakerSettingsModalFooterActions = () => {
  const {
    submitForm,
    resetForm
  } = useFormContext();
  return <SettingsModalFooterActions onSave={submitForm} onCancel={resetForm} />;
};
const formFooterCss = "f15jirc1";
const NotetakerSettingsFormFooter = () => {
  const {
    resetForm
  } = useFormContext();
  return <div className={formFooterCss}>
      <SubmitFormButton>
        {I18n.t('publisher.action.save_changes')}
      </SubmitFormButton>
      <Button decoration="secondary-outline" onClick={resetForm}>
        {I18n.t('publisher.action.cancel')}
      </Button>
    </div>;
};
const AccessControl = () => {
  const dispatch = useDispatch();
  const {
    showFooterHost,
    footerHost
  } = useSettingsModalLayoutOptional() ?? {};
  const {
    data: meetingIntelligenceData,
    isFetching
  } = useGetMeetingIntelligenceUserSettingsQuery({}, {
    refetchOnMountOrArgChange: true
  });
  const [updateOrganizationSettings, {
    isSuccess,
    reset
  }] = useUpdateOrganizationMeetingIntelligenceDataMutation();
  const initialNotetakerAccess = useMemo(() => meetingIntelligenceData?.adminDisabledNotetaker ? 'no_one' : 'everyone', [meetingIntelligenceData]);
  useEffect(() => {
    if (isSuccess) {
      dispatch(showSuccess(I18n.t('publisher.flash.changes_saved')));
      reset();
    }
  }, [dispatch, isSuccess, reset]);
  const onSubmit = async (values, actions) => {
    const {
      notetaker_access: notetakerAccess
    } = values;
    const adminDisabledNotetaker = notetakerAccess === 'no_one';
    const result = await updateOrganizationSettings({
      adminDisabledNotetaker
    });
    if (result.error) {
      dispatch(showError(I18n.t('publisher.organization_settings.notetaker_settings.update_failure')));
      return;
    }
    actions?.resetForm?.({
      values
    });
  };
  const options = [{
    label: I18n.t('publisher.organization_settings.notetaker_settings.everyone.title'),
    description: <div className={optionDescriptionCss}>
          <BodyText color="muted">
            {I18n.t('publisher.organization_settings.notetaker_settings.everyone.subtitle')}
          </BodyText>
        </div>,
    value: 'everyone'
  }, {
    label: I18n.t('publisher.organization_settings.notetaker_settings.no_one.title'),
    description: <div className={optionDescriptionCss}>
          <BodyText color="muted">
            {I18n.t('publisher.organization_settings.notetaker_settings.no_one.subtitle')}
          </BodyText>
        </div>,
    value: 'no_one'
  }];
  if (isFetching) {
    return <LoadingState />;
  }
  return <TabContainer>
      <TitleText level={3}>
        {I18n.t('publisher.organization_settings.notetaker_settings.description')}
      </TitleText>
      <Form initialValues={{
      notetaker_access: initialNotetakerAccess
    }} onSubmit={onSubmit} className={formCss}>
        <DirtyFormTracking />
        <RadioGroupField name="notetaker_access" options={options} />
        {showFooterHost ? footerHost != null && createPortal(<NotetakerSettingsModalFooterActions />, footerHost) : <NotetakerSettingsFormFooter />}
      </Form>
    </TabContainer>;
};
export const NotetakerSettingsPage = ({
  match
}) => {
  const pageTitle = `${I18n.t('publisher.admin.navigation.notetaker_settings')}`;
  const inSettingsModal = useIsSettingsModalLayout();
  usePageTitle(pageTitle);
  return <>
      {!inSettingsModal && <PageHeader eyebrow={<TitleText variant="eyebrow">
              {I18n.t('publisher.admin.navigation.notetaker_settings')}
            </TitleText>} title={I18n.t('publisher.admin.navigation.notetaker_access')} />}
      <Switch>
        <Redirect exact from="/app/admin/notetaker" to="/app/admin/notetaker/access" />
        <Route path={`${match.url}/access`} component={AccessControl} />
      </Switch>
    </>;
};

require("./notetaker_settings.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notetaker_settings.js");